import { Howl, Howler } from 'howler';

import { setVoiceCaption } from 'a/reducers/captions';
import store from '../activities/store';
import Env from './Env';

class AquaAudio {
  static playBackSpeed = 1;

  static stopAllAquaAudio() {
    Howler.stop();
  }

  static setPlaybackSpeed(speed) {
    AquaAudio.playBackSpeed = speed;
  }

  static unload() {
    try {
      // Howler.unload() also recreates the context, which can throw unless it's running
      if (Howler.usingWebAudio && Howler.ctx?.state === 'running') {
        Howler.unload();
      } else {
        // eslint-disable-next-line no-underscore-dangle
        Howler._howls.slice().forEach(howl => howl.unload());
      }
    } catch (error) {
      console.error('Failed to unload Howler:', error);
    }
  }

  constructor(soundSource, desc, options = {}) {
    this.loaded = false;
    this.loadFailed = false;

    this.onerror = options && options.onerror ? options.onerror : () => {
      if (options.onend) {
        options.onend();
      }
    };
    if (options) {
      this.onLoadCallBack = options.onLoadCallBack;
    }

    this.desc = desc;
    this.caption = options.caption || '';

    this.sound = new Howl({
      src: [soundSource],
      onloaderror: this.onLoadError,
      onload: () => {
        this.loaded = true;
        if (this.onLoadCallBack) {
          this.onLoadCallBack();
        }
      },
      format: ['mp3'],
      ...options,
      onend: () => {
        this.resetCaption();

        if (options.unloadAfterOnePlay) {
          this.sound.unload();
        }

        if (options.onend) {
          options.onend();
        }
      },
    });
    this.sound.load();

    if (Env.isLocalDev) {
      const localVolume = localStorage.getItem('volume');
      const localRate = localStorage.getItem('rate');
      this.sound.volume(localVolume !== null ? parseInt(localVolume, 10) : 1);
      this.sound.rate(localRate !== null ? parseInt(localRate, 10) : AquaAudio.playBackSpeed);
    }

    // Visibility change handling (pause on hide, resume on show) is done globally in index.js via startPageVisibilityEvents.
    // Per-instance listeners were removed — they were redundant with the global handler and caused double-play on iOS resume.
  }

  resetCaption() {
    if (!this.caption) return;
    const currentCaption = store.getState().captions.voice;
    if (this.caption === currentCaption) {
      store.dispatch(setVoiceCaption(''));
    }
  }

  /**
   * Play the audio file
   */
  play = () => {
    if (this.caption !== '') {
      store.dispatch(setVoiceCaption(this.caption));
    }
    this.sound.rate(AquaAudio.playBackSpeed);
    this.sound.play();
  };

  /**
   * Play an audio file after a specified delay
   * @param delay ms to wait before playing sound
   */
  playWithDelay(delay) {
    setTimeout(this.play, delay);
  }

  /**
   * Set the position of playback for a sound.
   * @param {number} time The position to move current playback to (in milliseconds).
   */
  seek = (time) => {
    this.sound.seek(time / 1000);
  };

  /**
   * stop the sound that is playing
   */
  stop = (withOnEndCallback = false) => {
    if (this.sound.playing()) {
      this.resetCaption();
      this.sound.stop();
      if (withOnEndCallback) {
        this.sound.onend();
      }
    }
  };

  // duration of loaded track in seconds. will return 0 if track hasn't loaded yet
  duration() {
    return this.sound.duration() / AquaAudio.playBackSpeed;
  }

  /**
   * Called if audio file failed to load
   * @param soundId id of sound that failed to load
   * @param message error message
   */
  onLoadError = (soundId, message) => {
    this.loadFailed = true;
    this.loadError = message;

    console.warn(
      `There was a problem loading ${this.desc} for playback: `,
      message,
    );
    this.onerror(); // callback to alert original caller that the load failed
  };

  // newVolume should be between 0(silent) and 1(full volume)
  setVolume(newVolume) {
    this.sound.volume(newVolume);
  }

  // newVolume should be between 0(silent) and 1(full volume)
  fadeVolume(newVolume, duration) {
    this.sound.fade(this.sound.volume(), newVolume, duration);
  }
}

export default AquaAudio;
